// frontend/src/pages/org/org-workspaces-page.tsx
// Read DESIGN.md before modifying this file. Blue info Alert pattern for the
// empty state; Title Case button labels.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useForm } from '@tanstack/react-form';
import { Info, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { FieldError } from '@/components/form/field-error';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWorkspace } from '@/contexts/workspace-context';
import { useSession } from '@/contexts/session-context';
import { workspaceNameSchema } from '@/lib/form/schemas';
import { PageContainer } from '@/components/page-container';
import { OrgWorkspaceTable } from './org-workspace-table';
import { useCreateWorkspace, useOrgWorkspaces } from './org-workspaces-queries';
import { emit } from '@/lib/analytics';

/**
 * Org landing page (spec Decision 2): table of every actor-visible team
 * workspace (server-side filtered: admins see all, members see assigned only).
 * Fetches ?kind=team so customer workspaces never appear here. Row click
 * enters that workspace's Dashboard. New Workspace reuses the existing
 * POST /workspaces create flow; errors surface as toasts.
 *
 * The search box filters client-side over workspace name + each channel's
 * name, phone, and IG handle (the list is loaded in full, no server
 * pagination); the token matching lives in OrgWorkspaceTable's react-table
 * global filter.
 */
export function OrgWorkspacesPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { refreshWorkspaces } = useWorkspace();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { role } = useSession();

  // Admins get the deprecated rows in the payload; the table hides them behind
  // its own Show deprecated switch (and disables it when there are none).
  const workspacesQuery = useOrgWorkspaces(orgId, 'team', role === 'admin');
  const rows = workspacesQuery.data?.workspaces ?? [];
  const loaded = !workspacesQuery.isPending;
  const createWorkspace = useCreateWorkspace(orgId);

  useEffect(() => {
    if (workspacesQuery.error) {
      toast.error(workspacesQuery.error.userMessage ?? 'Failed to load workspaces');
    }
  }, [workspacesQuery.error]);

  const form = useForm({
    defaultValues: { name: '' },
    onSubmit: async ({ value }) => {
      createWorkspace.mutate(
        { name: value.name.trim() },
        {
          onSuccess: async (ws) => {
            // Funnel stage-5 parity with workspace-switcher.tsx handleCreate.
            emit('workspace_created', {
              workspace_id: ws.id,
              // The public DTO no longer exposes createdAt (AIT-147); the event
              // fires at creation time, so stamping now keeps the registry shape.
              created_at: new Date().toISOString(),
            });
            toast.success('Workspace created');
            setDialogOpen(false);
            form.reset();
            await refreshWorkspaces();
          },
          onError: (err) => {
            toast.error(err.userMessage ?? 'Failed to create workspace');
          },
        },
      );
    },
  });

  return (
    <PageContainer variant="fill" className="gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Workspaces</h1>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open) form.reset();
            setDialogOpen(open);
          }}
        >
          <DialogTrigger
            render={
              <Button size="sm">
                <Plus className="size-3.5" />
                New Workspace
              </Button>
            }
          />
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New Workspace</DialogTitle>
            </DialogHeader>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="new-workspace-name">Workspace Name</Label>
                <form.Field
                  name="name"
                  validators={{ onChange: workspaceNameSchema, onSubmit: workspaceNameSchema }}
                >
                  {(field) => (
                    <>
                      <Input
                        id="new-workspace-name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="Acme WhatsApp"
                        autoFocus
                        aria-invalid={field.state.meta.errors.length > 0 || undefined}
                        aria-describedby={
                          field.state.meta.errors.length > 0
                            ? 'new-workspace-name-error'
                            : undefined
                        }
                      />
                      <FieldError
                        id="new-workspace-name-error"
                        errors={field.state.meta.errors}
                      />
                    </>
                  )}
                </form.Field>
              </div>
              <form.Subscribe
                selector={(s) => [s.canSubmit, s.isSubmitting, s.values.name] as const}
              >
                {([canSubmit, isSubmitting, name]) => (
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      !canSubmit ||
                      isSubmitting ||
                      createWorkspace.isPending ||
                      !name.trim()
                    }
                  >
                    {createWorkspace.isPending && (
                      <Loader2 className="size-3.5 animate-spin" />
                    )}
                    Create Workspace
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loaded && rows.length === 0 && (
        <Alert className="border-blue-500/30 bg-blue-500/5">
          <Info className="text-blue-500" />
          <AlertDescription>
            No workspaces yet. Create your first workspace to get started.
          </AlertDescription>
        </Alert>
      )}

      {rows.length > 0 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Workspace or channel"
            className="h-9 pl-8 text-sm"
            aria-label="Search workspaces"
          />
        </div>
      )}

      {rows.length > 0 && (
        <OrgWorkspaceTable rows={rows} usageUnit={workspacesQuery.data?.usageUnit} search={search} kind="team" />
      )}
    </PageContainer>
  );
}
